import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useDebounce from '../../../hooks/useDebounce';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Search, Download, Cloud, History, Warning, SwapHoriz } from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import ProceedingTable from '../components/ProceedingTable';
import ProceedingModalForm from '../components/ProceedingModalForm';
import LoadingLogo from '../../../components/LoadingLogo';
import OneDriveBulkImport from '../components/OneDriveBulkImport';
import BulkImportProgress from '../components/BulkImportProgress';
import ActiveJobsBadge from '../components/ActiveJobsBadge';
import proceedingService from '../services/proceedingService';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../hooks/useAuth';
import { companyService } from '../../companies';
import { retentionService } from '../../retentions';

const ProceedingList = () => {
  const { hasPermission } = usePermissions();
  const { user, isOwner } = useAuth();
  const navigate = useNavigate();
  const [proceedings, setProceedings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [retentions, setRetentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 2000);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [openModal, setOpenModal] = useState(false);
  const [selectedProceeding, setSelectedProceeding] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [oneDriveDialogOpen, setOneDriveDialogOpen] = useState(false);
  const [oneDriveMode, setOneDriveMode] = useState('new');
  const [activeJob, setActiveJob] = useState(null);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [retentionAlert, setRetentionAlert] = useState('');

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadRetentions(selectedCompany);
    } else {
      setRetentions([]);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (user?.companyId && !selectedCompany) {
      setSelectedCompany(user.companyId.toString());
    }
  }, [user?.companyId]);

  useEffect(() => {
    loadProceedings();
  }, [page, debouncedSearch, selectedCompany, startDate, endDate, sortBy, sortDir, retentionAlert]);

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  const loadRetentions = async (companyId) => {
    try {
      const response = await retentionService.getAll({ companyId, limit: 9999 });
      setRetentions(response.data);
    } catch (error) {
      console.error('Error al cargar retenciones:', error);
    }
  };

  const loadProceedings = async () => {
    try {
      setLoading(true);
      const response = await proceedingService.getAll({
        search: debouncedSearch,
        companyId: selectedCompany,
        page,
        limit: 10,
        startDate,
        endDate,
        sortBy,
        sortDir,
        ...(retentionAlert && { retentionAlert }),
      });
      setProceedings(response.data);
      setPagination(response.pagination);
    } catch (error) {
      showSnackbar('Error al cargar expedientes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedProceeding(null);
    setOpenModal(true);
  };

  const handleEdit = (proceeding) => {
    setSelectedProceeding(proceeding);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este expediente?')) {
      try {
        await proceedingService.delete(id);
        setProceedings(prev => prev.filter(p => p.id !== id));
        setPagination(prev => prev ? { ...prev, total: (prev.total || 1) - 1 } : prev);
        showSnackbar('Expediente eliminado exitosamente', 'success');
      } catch (error) {
        showSnackbar('Error al eliminar expediente', 'error');
      }
    }
  };

  const handleSave = async (data) => {
    try {
      if (selectedProceeding) {
        await proceedingService.update(selectedProceeding.id, data);
        setProceedings(prev => prev.map(p =>
          p.id === selectedProceeding.id ? { ...p, ...data } : p
        ));
        showSnackbar('Expediente actualizado exitosamente', 'success');
      } else {
        const created = await proceedingService.create(data);
        showSnackbar('Expediente creado exitosamente', 'success');
        setOpenModal(false);
        navigate(`/proceedings/${created.data.id}`);
        return;
      }
      setOpenModal(false);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar expediente', 'error');
      throw error;
    }
  };

  const handleSortChange = (field) => {
    if (field === sortBy) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenOneDrive = (mode) => {
    setOneDriveMode(mode);
    setOneDriveDialogOpen(true);
  };

  const handleCloseOneDrive = () => {
    setOneDriveDialogOpen(false);
    loadProceedings();
  };

  const handleJobClick = (job) => {
    setActiveJob(job);
    setProgressDialogOpen(true);
  };

  const handleCloseProgress = () => {
    setProgressDialogOpen(false);
    setActiveJob(null);
    loadProceedings();
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Polling: silently refresh every 30 seconds when no modal is open
  const pollingRef = useRef(null);
  useEffect(() => {
    pollingRef.current = setInterval(() => {
      if (!openModal) {
        loadProceedings();
      }
    }, 30000);
    return () => clearInterval(pollingRef.current);
  }, [debouncedSearch, selectedCompany, page, startDate, endDate, sortBy, sortDir, openModal, retentionAlert]);

  return (
    <Box>
      <PageHeader
        title="Expedientes"
        subtitle="Unidades documentales con su tabla de retencion, cajas y prestamos."
        stat={pagination?.total || 0}
        statLabel="Expedientes"
        actions={
          <>
            {isOwner && <ActiveJobsBadge onJobClick={handleJobClick} />}
            {isOwner && (
              <>
                <Button variant="outlined" startIcon={<History />} onClick={() => navigate('/proceedings/import-history')} size="small">
                  Historial
                </Button>
                <Button variant="outlined" startIcon={<Cloud />} onClick={() => handleOpenOneDrive('new')} size="small">
                  OneDrive (Nuevos)
                </Button>
                <Button variant="outlined" startIcon={<Cloud />} onClick={() => handleOpenOneDrive('existing')} size="small">
                  OneDrive (Existentes)
                </Button>
              </>
            )}
            <Button variant="outlined" startIcon={<Download />} onClick={() => proceedingService.exportExcel({ search, companyId: selectedCompany, startDate, endDate })}>
              Exportar Excel
            </Button>
            {hasPermission('proceeding.create') && (
              <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
                Nuevo Expediente
              </Button>
            )}
          </>
        }
      />

      <Box sx={{ bgcolor: '#f2f2f3', borderRadius: 0, p: '18px', border: '1px solid rgba(29,31,32,0.16)', mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Buscar</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por nombre o codigo..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {isOwner && (
            <Box>
              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Empresa</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedCompany}
                  onChange={(e) => { setSelectedCompany(e.target.value); setPage(1); }}
                  displayEmpty
                >
                  <MenuItem value="">Todas</MenuItem>
                  {companies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Fecha inicio</Typography>
            <TextField fullWidth size="small" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Fecha fin</Typography>
            <TextField fullWidth size="small" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Box>
        </Box>
      </Box>

      {/* Alertas de retencion */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <Button
          variant={retentionAlert === 'transfer' ? 'contained' : 'outlined'}
          startIcon={<SwapHoriz />}
          onClick={() => { setRetentionAlert(prev => prev === 'transfer' ? '' : 'transfer'); setPage(1); }}
          size="small"
          color={retentionAlert === 'transfer' ? 'warning' : 'inherit'}
        >
          Proximos a trasladar
        </Button>
        <Button
          variant={retentionAlert === 'elimination' ? 'contained' : 'outlined'}
          startIcon={<Warning />}
          onClick={() => { setRetentionAlert(prev => prev === 'elimination' ? '' : 'elimination'); setPage(1); }}
          size="small"
          color={retentionAlert === 'elimination' ? 'error' : 'inherit'}
        >
          Proximos a eliminar
        </Button>
        {retentionAlert && (
          <Button
            variant="outlined"
            onClick={() => { setRetentionAlert(''); setPage(1); }}
            size="small"
          >
            Limpiar filtro
          </Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <LoadingLogo size={120} />
        </Box>
      ) : (
        <ProceedingTable
          proceedings={proceedings}
          onEdit={handleEdit}
          onDelete={handleDelete}
          page={page}
          onPageChange={setPage}
          pagination={pagination}
          canView={hasPermission('proceeding.view')}
          canEdit={hasPermission('proceeding.update')}
          canDelete={hasPermission('proceeding.delete')}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={handleSortChange}
        />
      )}

      <ProceedingModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        proceeding={selectedProceeding}
        companies={companies}
        retentions={retentions}
        onCompanyChange={loadRetentions}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isOwner && (
        <OneDriveBulkImport
          open={oneDriveDialogOpen}
          onClose={handleCloseOneDrive}
          companies={companies}
          mode={oneDriveMode}
        />
      )}

      {activeJob && (
        <Dialog open={progressDialogOpen} onClose={handleCloseProgress} maxWidth="md" fullWidth>
          <DialogTitle>Progreso de Importación</DialogTitle>
          <DialogContent>
            <BulkImportProgress jobId={activeJob.id} onClose={handleCloseProgress} />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default ProceedingList;
